import { FunctionComponent } from 'react';
import clsx from 'clsx';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, useMediaQuery } from '@material-ui/core';
import { faPlus as faPlusSolid } from '@fortawesome/pro-solid-svg-icons';
import { Header } from 'components/UI';
import Filter from 'components/UI/Filter';
import { Product } from '@spike/product-model';
import ProductVendor from 'model/ProductVendor';
import ProductCategory from 'model/ProductCategory';
import { faHandshake, faPaw } from '@fortawesome/pro-regular-svg-icons';
import VendorsFilter from './VendorsFilter';
import SearchComponent from '../UI/Search';
import StockStatusFilter from 'components/Products/Stock/StockStatusFilter';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';
import uniqWith from 'lodash/uniqWith';
import Button from 'components/UI/V3/Button/Button';

export interface Properties {
    selectedViewId: string;
    vendorsActive: boolean;
    vendorsDeactive: boolean;
    products: Array<Product>;
    vendors: Array<ProductVendor>;
    categories: Array<ProductCategory>;
    selectedStockStatuses?: Array<string>;
    selectedStockMarketplaceIds?: Array<number>;
    onSearch: (text: string) => void;
    onAddProduct: () => void;
    onAddVendor: () => void;
    onAddCategory: () => void;
    onAddStock?: () => void;
    onSelectProductCategory: (ids: (number | undefined)[], all: boolean) => void;
    onSelectVendor: (active: boolean, deactive: boolean) => void;
    onSelectStockStatuses?: (selectedStatusIds: Array<string>) => void;
    onSelectStockMarketplaces?: (selectedMarketplaceIds: Array<number>) => void;
    loading: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            background: '#FAFAFA'
        },
        stockDescription: {
            margin: '20px 0 0',
            color: '#000',
            fontSize: '15px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '140%',
            [theme.breakpoints.down(768)]: {
                paddingLeft: 16,
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: 27,
                paddingRight: 40
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: 43,
                paddingRight: 65
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: 57,
                paddingRight: 87
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: 87,
                paddingRight: 132
            }
        },
        headerRow: {
            'width': '100% !important',
            '& .MuiGrid-container .MuiGrid-item:last-child:not(:only-child)': {
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
            },
            [theme.breakpoints.down(768)]: {
                paddingTop: '10px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingTop: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingTop: '24px'
            }
        },
        addButtonContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
        },
        stockToolbar: {
            paddingTop: '39px !important'
        },
        toolbarWrapper: {
            width: '100%',
            [theme.breakpoints.down(768)]: {
                paddingTop: 10,
                paddingLeft: 16,
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingTop: '13px',
                paddingRight: '40px',
                paddingLeft: '27px'
            },
            [theme.breakpoints.only('md')]: {
                paddingTop: '21px',
                paddingRight: '65px',
                paddingLeft: '43px'
            },
            [theme.breakpoints.only('lg')]: {
                paddingTop: '28px',
                paddingRight: '87px',
                paddingLeft: '57px'
            },
            [theme.breakpoints.only('xl')]: {
                paddingTop: '28px',
                paddingRight: '132px',
                paddingLeft: '87px'
            }
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: '#F1F1F1',
            borderRadius: '12px',
            padding: '15px 22px',
            gap: '16px'
        },
        search: {
            'width': '40% !important',
            'flex': '0 0 40%',
            '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                width: '100% !important'
            }
        },
        stockFilters: {
            'display': 'flex',
            'alignItems': 'center',
            'gap': '16px',
            'marginLeft': 'auto',
            '& $filter': {
                marginLeft: 0
            }
        },
        filter: {
            'flex': '0 0 auto',
            'marginLeft': 'auto',
            // Only the trigger — not Apply / options inside the dropdown
            '& > button': {
                backgroundColor: '#FFFFFF !important',
                [theme.breakpoints.down('md')]: {
                    height: '34px !important',
                    minHeight: '34px',
                    paddingTop: 0,
                    paddingBottom: 0
                },
                [theme.breakpoints.up('lg')]: {
                    height: '43px !important',
                    minHeight: '43px',
                    paddingTop: 0,
                    paddingBottom: 0
                }
            }
        }
    })
);

const getTitle = (selectedViewId: string) => {
    switch (selectedViewId) {
        case 'vendors':
            return 'Vendors';
        case 'categories':
            return 'Categories';
        case 'stock':
            return 'Stock';
        default:
            return 'Products';
    }
};

export const ProductsHeader: FunctionComponent<Properties> = props => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:767.95px)');

    const getCategories = () => {
        if (props.categories.length > 0) {
            return props.categories
                .filter(category => category.id != null)
                .map(category => ({
                    id: category.id!,
                    name: category.name
                }));
        }

        return uniqWith(
            props.products.filter(product => product.category).map(product => product.category!),
            (category, otherCategory) => category.id === otherCategory.id
        );
    };

    const handleSelectFilterProduct = (ids: (number | undefined)[], all: boolean) => {
        props.onSelectProductCategory(ids, all);
    };

    const addButton = (() => {
        if (props.selectedViewId === 'products') {
            return (
                <Box className={classes.addButtonContainer}>
                    <Button
                        label="Add Product"
                        leftIcon={faPlusSolid}
                        onlyIcon={isMobile}
                        onClick={props.onAddProduct}
                    />
                </Box>
            );
        }

        if (props.selectedViewId === 'stock') {
            return undefined;
        }

        if (props.selectedViewId === 'vendors') {
            return (
                <Box className={classes.addButtonContainer}>
                    <Button
                        label="Add Vendor"
                        leftIcon={faPlusSolid}
                        onlyIcon={isMobile}
                        onClick={props.onAddVendor}
                    />
                </Box>
            );
        }

        return (
            <Box className={classes.addButtonContainer}>
                <Button
                    label="Add Category"
                    leftIcon={faPlusSolid}
                    onlyIcon={isMobile}
                    onClick={props.onAddCategory}
                />
            </Box>
        );
    })();

    return (
        <Box className={classes.container}>
            <Header
                title={getTitle(props.selectedViewId)}
                className={classes.headerRow}
                rightElements={addButton}
            />
            {props.selectedViewId === 'stock' && (
                <p className={classes.stockDescription}>Manage your inventory by adjusting product stock levels.</p>
            )}
            <Box className={clsx(classes.toolbarWrapper, props.selectedViewId === 'stock' && classes.stockToolbar)}>
                <Box className={classes.toolbar}>
                    <SearchComponent
                        dashboard={true}
                        viewSelected={props.selectedViewId}
                        onSearch={search => props.onSearch(search)}
                        placeholder="Search item"
                        className={classes.search}
                    />
                    {props.selectedViewId === 'products' && (
                        <Filter
                            label="Categories"
                            icon={faPaw}
                            options={getCategories()}
                            onSelect={(ids, all) => handleSelectFilterProduct(ids, all)}
                            className={classes.filter}
                        />
                    )}
                    {props.selectedViewId === 'vendors' && (
                        <VendorsFilter
                            label="Vendors"
                            icon={faHandshake}
                            active={props.vendorsActive}
                            deactive={props.vendorsDeactive}
                            onSelect={(active, deactive) => props.onSelectVendor(active, deactive)}
                            className={classes.filter}
                        />
                    )}
                    {props.selectedViewId === 'stock' && (
                        <Box className={classes.stockFilters}>
                            <StockStatusFilter
                                selected={props.selectedStockStatuses ?? []}
                                onChangeSelection={props.onSelectStockStatuses}
                                className={classes.filter}
                            />
                            <OrganizationMarketplacesFilter
                                selected={props.selectedStockMarketplaceIds ?? []}
                                onChangeSelection={props.onSelectStockMarketplaces}
                                className={classes.filter}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ProductsHeader;
